import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Tag, Tooltip, Skeleton, Result, Button, Typography, Empty } from 'antd';
import {
  MedicineBoxOutlined,
  UserOutlined,
  SwapOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  getCicloActual,
  getResumen,
  getIncidencias,
  ESTADO_PENDIENTE,
  INCIDENCIA_TOOLTIPS,
  INCIDENCIA_COLORS,
  type ReconciliacionCiclo,
  type ReconciliacionResumen,
} from '../../api/reconciliacionApi';

const { Text, Title } = Typography;

interface DashboardTabProps {
  onTabChange: (key: string) => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ onTabChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [ciclo, setCiclo] = useState<ReconciliacionCiclo | null>(null);
  const [resumen, setResumen] = useState<ReconciliacionResumen | null>(null);
  const [incidenciasPorTipo, setIncidenciasPorTipo] = useState<Record<string, number>>({});

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const cicloActual = await getCicloActual();
      setCiclo(cicloActual);

      const [resumenData, incidencias] = await Promise.all([
        getResumen(cicloActual.id),
        getIncidencias(undefined, ESTADO_PENDIENTE),
      ]);
      setResumen(resumenData);

      // Agrupar incidencias por tipo
      const porTipo: Record<string, number> = {};
      incidencias.forEach(inc => {
        porTipo[inc.tipo] = (porTipo[inc.tipo] || 0) + 1;
      });
      setIncidenciasPorTipo(porTipo);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setCiclo(null);
        setResumen(null);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Row gutter={[24, 24]}>
          {[1, 2, 3, 4].map(i => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Col xs={24} sm={12} md={8} lg={4} key={i}>
              <Card><Skeleton active paragraph={{ rows: 2 }} /></Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar el dashboard"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  if (!ciclo) {
    return (
      <Empty description="El motor aún no se ha ejecutado">
        <Button type="primary" onClick={() => onTabChange('historico')}>
          Ir a Histórico
        </Button>
      </Empty>
    );
  }

  const pendientes = (porEstado: Record<number, number>) => porEstado[ESTADO_PENDIENTE] || 0;

  const ambitoCards = [
    { key: 'farmacias', icon: <MedicineBoxOutlined />, label: 'Farmacias', total: resumen?.totalFarmacias || 0, pending: pendientes(resumen?.farmaciasPorEstado || {}) },
    { key: 'farmaceuticos', icon: <UserOutlined />, label: 'Farmacéuticos', total: resumen?.totalFarmaceuticos || 0, pending: pendientes(resumen?.farmaceuticosPorEstado || {}) },
    { key: 'transmisiones', icon: <SwapOutlined />, label: 'Transmisiones', total: resumen?.totalTransmisiones || 0, pending: pendientes(resumen?.transmisionesPorEstado || {}) },
    { key: 'empleados', icon: <TeamOutlined />, label: 'Empleados', total: resumen?.totalEmpleados || 0, pending: pendientes(resumen?.empleadosPorEstado || {}) },
    { key: 'traslados', icon: <EnvironmentOutlined />, label: 'Traslados', total: resumen?.totalTraslados || 0, pending: pendientes(resumen?.trasladosPorEstado || {}) },
  ];

  const tiposIncidencia = ['E1', 'E2', 'I1', 'I2', 'F7'];

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Bloque 1: Última ejecución */}
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={<span><ClockCircleOutlined style={{ marginRight: 8 }} />Última ejecución del motor</span>}
            size="small"
          >
            <Row gutter={24}>
              <Col>
                <Statistic title="Fecha" value={new Date(ciclo.timestamp).toLocaleString('es-ES')} />
              </Col>
              <Col>
                <Statistic title="Modo" value={ciclo.modo} />
              </Col>
              <Col>
                <Statistic title="Farmacias analizadas" value={ciclo.farmaciasAnalizadas} />
              </Col>
              <Col>
                <Statistic title="Operaciones" value={ciclo.totalOperaciones} />
              </Col>
              <Col>
                <Statistic title="Excepciones" value={ciclo.totalExcepciones} valueStyle={ciclo.totalExcepciones > 0 ? { color: '#cf1322' } : undefined} />
              </Col>
              <Col>
                <Statistic title="Invariantes" value={ciclo.totalInvariantes} valueStyle={ciclo.totalInvariantes > 0 ? { color: '#cf1322' } : undefined} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Bloque 2: Casos pendientes por ámbito */}
      <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>Casos pendientes por ámbito</Title>
      <Row gutter={[16, 16]}>
        {ambitoCards.map(a => (
          <Col xs={24} sm={12} md={8} lg={4} key={a.key}>
            <Card
              hoverable
              onClick={() => onTabChange(a.key)}
              style={{ textAlign: 'center' }}
              bodyStyle={{ padding: '16px 12px' }}
            >
              <div style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }}>{a.icon}</div>
              <Text strong>{a.label}</Text>
              <div style={{ fontSize: 28, fontWeight: 700, margin: '8px 0', color: a.pending > 0 ? '#fa8c16' : '#52c41a' }}>
                {a.pending}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>pendientes de {a.total} totales</Text>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Bloque 3: Incidencias pendientes por tipo */}
      <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
        <WarningOutlined style={{ marginRight: 8 }} />
        Incidencias pendientes por tipo
      </Title>
      <Row gutter={[16, 16]}>
        {tiposIncidencia.map(tipo => {
          const count = incidenciasPorTipo[tipo] || 0;
          return (
            <Col key={tipo}>
              <Card size="small" bodyStyle={{ padding: '12px 20px', textAlign: 'center' }}>
                <Tooltip title={INCIDENCIA_TOOLTIPS[tipo]}>
                  <Tag color={count > 0 ? INCIDENCIA_COLORS[tipo] : 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
                    {tipo}
                  </Tag>
                </Tooltip>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 8 }}>{count}</div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Bloque 4: Tendencia (tabla simple en v1) */}
      {/* TODO: añadir gráfico de tendencia con las últimas N ejecuciones */}
    </div>
  );
};

export default DashboardTab;
